import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function CheckoutForm({ creatorId, creatorName, amount, onSuccess, onCancel, token }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Confirm the payment
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Confirm subscription on backend
        const formData = new FormData();
        formData.append('payment_intent_id', paymentIntent.id);
        formData.append('creator_id', creatorId);

        await axios.post(`${API_URL}/api/stripe/confirm-subscription`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        onSuccess();
      }
    } catch (err) {
      setError(err.message || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="mb-4">
        <p className="text-lg font-semibold mb-2">Subscribe to {creatorName}</p>
        <p className="text-gray-600">Premium tier: ${amount.toFixed(2)}/month</p>
      </div>

      <PaymentElement />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-button">
          {error}
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-button font-semibold hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 bg-primary text-white px-4 py-3 rounded-button font-semibold hover:shadow-hover disabled:opacity-50"
        >
          {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
        </button>
      </div>
    </form>
  );
}

export default function PaymentModal({ isOpen, creatorId, creatorName, amount, onSuccess, onClose, token }) {
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (isOpen && creatorId) {
      fetchPaymentIntent();
    }
  }, [isOpen, creatorId]);

  const fetchPaymentIntent = async () => {
    try {
      setLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append('creator_id', creatorId);

      const response = await axios.post(`${API_URL}/api/stripe/create-payment-intent`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setClientSecret(response.data.clientSecret);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initialize payment');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#FF6B35',
        colorBackground: '#FFFBF5',
        colorText: '#1A1A1A',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: '12px'
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-card shadow-2xl max-w-md w-full p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Preparing payment...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="bg-gray-200 text-gray-700 px-6 py-2 rounded-button font-semibold hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        ) : clientSecret ? (
          <Elements stripe={stripePromise} options={options}>
            <CheckoutForm
              creatorId={creatorId}
              creatorName={creatorName}
              amount={amount}
              onSuccess={onSuccess}
              onCancel={onClose}
              token={token}
            />
          </Elements>
        ) : null}
      </div>
    </div>
  );
}
